/**
 * Orders the store raises for the purchaser, and tallying the goods in.
 *
 * The store keeper writes what is needed, prints the order in two halves
 * (one goes with whoever is buying, one stays in the store), and when the
 * goods arrive he ticks each line off with what actually came and what it
 * cost. That tally is the purchase slip; the shelf moves then and not before.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR, formatServerTime } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { ItemPicker, packs } from './ItemPicker.jsx';
import { DateFilter, useDateFilter } from './DateFilter.jsx';

export function OrdersTab({ items, admin = false }) {
  const qc = useQueryClient();
  const dates = useDateFilter();
  const open = useQuery({ queryKey: ['store', 'orders', dates.query], queryFn: () => api.get(`/store/orders?limit=100${dates.query}`) });
  const done = useQuery({ queryKey: ['store', 'orders', 'done', dates.query], queryFn: () => api.get(`/store/orders?done=1&limit=${dates.range ? 500 : 20}${dates.query}`) });
  const [draft, setDraft] = useState({ supplier: 'Local market', note: '', lines: [] });
  const [printing, setPrinting] = useState(null);
  const [tallying, setTallying] = useState(null);
  // An admin correcting an order nothing has arrived for yet, loaded into the form.
  const [editing, setEditing] = useState(null);

  const startEdit = async (o) => {
    const { order, lines } = await api.get(`/store/orders/${o.id}`);
    const byId = new Map(items.map((i) => [i.id, i]));
    setEditing({ id: order.id, number: order.po_number });
    setDraft({
      supplier: o.supplier_name || 'Local market',
      note: order.note || '',
      lines: lines.map((l) => {
        const it = byId.get(l.ingredientId);
        return { ingredientId: l.ingredientId, name: l.name, packName: l.packName, store: it?.store?.packs ?? 0, low: it?.low ?? false, packs: String(l.packs), note: l.note || '' };
      }),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const stopEdit = () => {
    setEditing(null);
    setDraft({ supplier: 'Local market', note: '', lines: [] });
  };

  const raise = useMutation({
    mutationFn: () => {
      const body = { supplierName: draft.supplier, note: draft.note, lines: draft.lines.map((l) => ({ ingredientId: l.ingredientId, packs: Number(l.packs), note: l.note || undefined })) };
      return editing ? api.put(`/store/orders/${editing.id}`, body) : api.post('/store/orders', body);
    },
    onSuccess: (res) => {
      setDraft({ supplier: draft.supplier, note: '', lines: [] });
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['store', 'orders'] });
      setPrinting(res.id);
    },
  });
  const edit = (i, patch) => setDraft({ ...draft, lines: draft.lines.map((l, k) => (k === i ? { ...l, ...patch } : l)) });
  const ready = draft.lines.length > 0 && draft.lines.every((l) => Number(l.packs) > 0);

  if (tallying) return <Tally orderId={tallying} items={items} onClose={() => setTallying(null)} />;

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <section className="rounded-xl border border-rule bg-surface p-4 lg:col-span-3">
        {editing ? (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2">
            <span className="text-sm text-ink">
              Editing order <span className="font-semibold">{editing.number}</span>. Saving replaces its lines and reprints it.
            </span>
            <Button size="sm" variant="secondary" onClick={stopEdit}>
              Cancel edit
            </Button>
          </div>
        ) : (
          <h2 className="mb-1 text-sm font-medium text-ink">Raise an order</h2>
        )}
        <p className="mb-3 text-xs text-ink-3">What to buy and how many packs. It prints in two halves: one for whoever is buying, one for the store. Nothing goes on the shelf until the goods are tallied in.</p>
        <div className="mb-3 grid gap-2 sm:grid-cols-2">
          <label className="text-sm text-ink-2">
            Buy from
            <input value={draft.supplier} onChange={(e) => setDraft({ ...draft, supplier: e.target.value })} className="mt-1 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
          </label>
          <label className="text-sm text-ink-2">
            Note
            <input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="needed by, who is going…" className="mt-1 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
          </label>
        </div>
        <ItemPicker items={items} onPick={(it) => !draft.lines.some((l) => l.ingredientId === it.id) && setDraft({ ...draft, lines: [...draft.lines, { ingredientId: it.id, name: it.name, packName: it.packName, store: it.store.packs, low: it.low, packs: '1', note: '' }] })} placeholder="Add an item to the order…" />
        {draft.lines.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-3">
                <th className="py-1 font-medium">Item</th>
                <th className="py-1 text-right font-medium">In store</th>
                <th className="py-1 text-right font-medium">Packs</th>
                <th className="py-1 font-medium">Note</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {draft.lines.map((l, i) => (
                <tr key={l.ingredientId} className="border-t border-rule">
                  <td className="py-2 text-ink">
                    {l.name}
                    <div className="text-xs text-ink-3">{l.packName}</div>
                  </td>
                  <td className={`py-2 text-right tabular-nums ${l.low ? 'text-warn' : 'text-ink-3'}`}>{packs(l.store)}</td>
                  <td className="py-2 text-right">
                    <input type="number" step="any" min="0" value={l.packs} onChange={(e) => edit(i, { packs: e.target.value })} aria-label={`Packs of ${l.name}`} className="h-10 w-20 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm text-ink" />
                  </td>
                  <td className="py-2">
                    <input value={l.note} onChange={(e) => edit(i, { note: e.target.value })} placeholder="brand, size…" className="h-10 w-full rounded-lg border border-rule bg-surface-3 px-2 text-sm text-ink" />
                  </td>
                  <td className="py-2 text-right">
                    <button type="button" onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, k) => k !== i) })} aria-label={`Remove ${l.name}`} className="text-xs text-ink-3 hover:text-bad">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {raise.error && (
          <p role="alert" className="mt-2 text-sm text-bad">
            {raise.error.message}
          </p>
        )}
        <div className="mt-3 flex justify-end">
          <Button onClick={() => raise.mutate()} disabled={!ready} busy={raise.isPending}>
            {editing ? 'Save changes and print' : 'Raise order and print'}
          </Button>
        </div>
      </section>

      <section className="space-y-4 lg:col-span-2">
        <div className="rounded-xl border border-rule bg-surface p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-medium text-ink-2">Waiting for goods</h2>
            <DateFilter range={dates.range} onChange={dates.setRange} />
          </div>
          {open.isLoading && <Spinner />}
          <ul className="space-y-1.5 text-sm">
            {(open.data?.orders ?? []).map((o) => (
              <li key={o.id} className="rounded-lg border border-rule px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span>
                    <span className="text-ink">{o.po_number}</span>
                    <span className="text-ink-3">
                      {' '}
                      · {o.supplier_name} · {o.lines} item{o.lines === 1 ? '' : 's'}
                      {o.status === 'partially_received' ? ` · ${o.lines_done}/${o.lines} in` : ''}
                    </span>
                  </span>
                  <span className="flex shrink-0 gap-1">
                    <Button size="sm" variant="secondary" onClick={() => setPrinting(o.id)}>
                      Print
                    </Button>
                    {admin && o.status === 'sent' && (
                      <Button size="sm" variant="secondary" onClick={() => startEdit(o)} aria-label={`Edit order ${o.po_number}`}>
                        Edit
                      </Button>
                    )}
                    <Button size="sm" onClick={() => setTallying(o.id)}>
                      Goods arrived
                    </Button>
                  </span>
                </div>
                <div className="text-xs text-ink-3">
                  {formatServerTime(o.created_at)} · {o.raised_by}
                  {o.notes && o.notes !== 'store:' ? ` · ${String(o.notes).replace(/^store:/, '')}` : ''}
                </div>
              </li>
            ))}
            {open.data && open.data.orders.length === 0 && <li className="text-ink-3">No orders waiting.</li>}
          </ul>
        </div>
        <div className="rounded-xl border border-rule bg-surface p-4">
          <h2 className="mb-3 text-sm font-medium text-ink-2">{dates.range ? 'Done on those days' : 'Done'}</h2>
          <ul className="space-y-1 text-xs text-ink-3">
            {(done.data?.orders ?? []).map((o) => (
              <li key={o.id} className="flex justify-between gap-2">
                <span>
                  <span className="text-ink-2">{o.po_number}</span> · {o.supplier_name} · {o.status === 'cancelled' ? 'cancelled' : 'received'}
                </span>
                <span>{formatServerTime(o.created_at)}</span>
              </li>
            ))}
            {done.data && done.data.orders.length === 0 && <li>Nothing yet.</li>}
          </ul>
        </div>
      </section>
      {printing && <OrderSlipSheet orderId={printing} onClose={() => setPrinting(null)} />}
    </div>
  );
}

/** The goods arrived: each line as it actually came, and what it cost. */
function Tally({ orderId, items, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['store', 'order', orderId], queryFn: () => api.get(`/store/orders/${orderId}`) });
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState('');
  const [printing, setPrinting] = useState(null);
  const [closing, setClosing] = useState(false);
  const receive = useMutation({
    mutationFn: (payment) => api.post(`/store/orders/${orderId}/receive`, { payment, note, lines: rows.filter((r) => Number(r.packs) > 0).map((r) => ({ ingredientId: r.ingredientId, packs: Number(r.packs), ratePerPack: Number(r.ratePerPack) })) }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['store'] });
      setPrinting(res);
    },
  });
  const close = useMutation({ mutationFn: () => api.post(`/store/orders/${orderId}/close`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['store', 'orders'] }); onClose(); } });
  if (isLoading || !data) return <Spinner />;
  const lines = rows ?? data.lines.map((l) => ({ ingredientId: l.ingredientId, name: l.name, packName: l.packName, ordered: l.packs, before: l.receivedPacks, packs: String(Math.max(0, l.packs - l.receivedPacks)), ratePerPack: l.lastPackPrice ?? '' }));
  if (!rows) setRows(lines);
  const edit = (i, patch) => setRows(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const active = lines.filter((l) => Number(l.packs) > 0);
  const ready = active.length > 0 && active.every((l) => Number(l.ratePerPack) >= 0 && l.ratePerPack !== '');
  const total = active.reduce((s, l) => s + Number(l.packs) * (Number(l.ratePerPack) || 0), 0);

  if (printing) {
    return (
      <div className="rounded-xl border border-rule bg-surface p-4">
        <p className="text-sm text-good">
          Tallied in as {printing.number} — {formatPKR(printing.total)} onto the store shelf{printing.onCredit ? ' on credit' : ''}. {printing.orderStatus === 'received' ? 'The order is complete.' : 'The order stays open for what has not come yet.'}
        </p>
        <div className="mt-3">
          <Button onClick={onClose}>Back to orders</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-brand-400/50 bg-surface p-4">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-medium text-ink">
          Goods arrived for {data.order.po_number} <span className="text-ink-3">· {data.order.supplier_name}</span>
        </h2>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onClose}>
          Back
        </Button>
      </div>
      <p className="mb-3 text-xs text-ink-3">Tick each line off with what actually came and the price per pack paid. Leave a line at 0 if it did not come; the order stays open for it.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-ink-3">
            <th className="py-1 font-medium">Item</th>
            <th className="py-1 text-right font-medium">Ordered</th>
            <th className="py-1 text-right font-medium">Came now</th>
            <th className="py-1 text-right font-medium">Rs / pack</th>
            <th className="py-1 text-right font-medium">Line</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.ingredientId} className="border-t border-rule">
              <td className="py-2 text-ink">
                {l.name}
                <div className="text-xs text-ink-3">
                  {l.packName}
                  {l.before > 0 ? ` · ${packs(l.before)} came earlier` : ''}
                </div>
              </td>
              <td className="py-2 text-right tabular-nums text-ink-3">{packs(l.ordered)}</td>
              <td className="py-2 text-right">
                <input type="number" step="any" min="0" value={l.packs} onChange={(e) => edit(i, { packs: e.target.value })} aria-label={`Packs of ${l.name} that came`} className="h-10 w-20 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm text-ink" />
              </td>
              <td className="py-2 text-right">
                <input type="number" step="any" min="0" value={l.ratePerPack} onChange={(e) => edit(i, { ratePerPack: e.target.value })} aria-label={`Price per pack of ${l.name}`} className="h-10 w-24 rounded-lg border border-rule bg-surface-3 px-2 text-right text-sm text-ink" />
              </td>
              <td className="py-2 text-right tabular-nums text-ink-2">{formatPKR((Number(l.packs) || 0) * (Number(l.ratePerPack) || 0))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3">
        <ItemPicker items={items} onPick={(it) => !lines.some((l) => l.ingredientId === it.id) && setRows([...lines, { ingredientId: it.id, name: it.name, packName: it.packName, ordered: 0, before: 0, packs: '1', ratePerPack: it.packPrice || '' }])} placeholder="Something came that was not on the order…" />
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (slip number, who paid…)" className="mt-3 h-10 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink" />
      {receive.error && (
        <p role="alert" className="mt-2 text-sm text-bad">
          {receive.error.message}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="mr-auto text-sm text-ink-2">
          Total <span className="text-lg font-semibold tabular-nums text-ink">{formatPKR(total)}</span>
        </span>
        {closing ? (
          <span className="flex items-center gap-2 text-sm text-ink-2">
            Nothing more is coming? This closes the order.
            <Button size="sm" variant="secondary" onClick={() => setClosing(false)}>
              Keep it open
            </Button>
            <Button size="sm" variant="danger" onClick={() => close.mutate()} busy={close.isPending}>
              Close it
            </Button>
          </span>
        ) : (
          <Button variant="ghost" onClick={() => setClosing(true)}>
            Close order as is
          </Button>
        )}
        <Button variant="secondary" disabled={!ready} busy={receive.isPending && receive.variables === 'credit'} onClick={() => receive.mutate('credit')}>
          Tally in on credit
        </Button>
        <Button disabled={!ready} busy={receive.isPending && receive.variables === 'paid'} onClick={() => receive.mutate('paid')}>
          Tally in (paid)
        </Button>
      </div>
    </div>
  );
}

/** The order on A4, two halves: the purchaser's copy and the store's. */
export function OrderSlipSheet({ orderId, onClose }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['store', 'order', orderId], queryFn: () => api.get(`/store/orders/${orderId}`) });
  return (
    <div role="dialog" aria-modal="true" aria-label="Print order" className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl bg-surface p-4" onClick={(e) => e.stopPropagation()}>
        <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-rule bg-white p-4 text-black">
          {isLoading && <Spinner />}
          {error && (
            <p role="alert" className="text-sm text-bad">
              {error.message}
            </p>
          )}
          {data && <OrderSlip data={data} />}
        </div>
        <div className="no-print mt-3 flex gap-2">
          <Button variant="secondary" size="lg" className="flex-1" onClick={onClose}>
            Close
          </Button>
          <Button size="lg" className="flex-1" disabled={!data} onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>
    </div>
  );
}

function OrderSlip({ data }) {
  const { order, lines } = data;
  const Copy = ({ label }) => (
    <div style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif', color: '#000', padding: '4mm 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '2px solid #000', paddingBottom: 4 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Food Run · Purchase order</div>
          <div style={{ fontSize: 12 }}>
            {order.po_number} · {order.supplier_name} · {formatServerTime(order.created_at)} · raised by {order.raised_by || ''}
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{label} copy</div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 6 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #000' }}>
            <th style={{ padding: '3px 0' }}>#</th>
            <th style={{ padding: '3px 0' }}>Item</th>
            <th style={{ padding: '3px 0', textAlign: 'right' }}>Packs</th>
            <th style={{ padding: '3px 0', textAlign: 'right', width: 70 }}>Came</th>
            <th style={{ padding: '3px 0', textAlign: 'right', width: 80 }}>Rs / pack</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.id} style={{ borderBottom: '1px solid #bbb' }}>
              <td style={{ padding: '4px 0' }}>{i + 1}</td>
              <td style={{ padding: '4px 0' }}>
                {l.name}
                {l.note ? <span style={{ fontSize: 11 }}> · {l.note}</span> : ''}
              </td>
              <td style={{ padding: '4px 0', textAlign: 'right' }}>
                {packs(l.packs)} × {l.packName}
              </td>
              <td style={{ padding: '4px 0', textAlign: 'right' }}>&nbsp;</td>
              <td style={{ padding: '4px 0', textAlign: 'right' }}>&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
      {order.note && <div style={{ fontSize: 12, marginTop: 6 }}>Note: {order.note}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginTop: 22, fontSize: 12 }}>
        <div style={{ flex: 1, borderTop: '1px solid #000', paddingTop: 3 }}>Store</div>
        <div style={{ flex: 1, borderTop: '1px solid #000', paddingTop: 3 }}>Purchaser</div>
      </div>
    </div>
  );
  return (
    <div className="statement">
      <Copy label="Purchaser" />
      <div style={{ borderTop: '2px dashed #000', margin: '6mm 0', textAlign: 'center', fontSize: 10, color: '#000' }}>✂ cut here</div>
      <Copy label="Store" />
    </div>
  );
}
